import ThemeToggle from './shared/ThemeToggle';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import logo from '../assets/logos/logo.png';
import WarehouseManagement from './warehouse/WarehouseManagement';
import HomePage from './home/HomePage';
import StoreOrdersPanel from './StoreOrdersPanel';
import StoreWastePanel from './StoreWastePanel';
import PlatformIntegration from './PlatformIntegration';
import PromotionsManagement from './PromotionsManagement';
import ECRFiscalDocuments from './ECRFiscalDocuments';
import POSTerminals from './POSTerminals';
import StoreAdminLayout from './shared/StoreAdminLayout';
import { isStoreAdminRole, resolveStoreTab, storeHasDelivery, storeUsesOxygen, type StoreTabId } from './shared/StoreNavigation';
import { confirmDiscard, hasUnsavedChanges } from '../utils/unsavedChanges';
import FloorPlanEditor from './dineIn/FloorPlanEditor';
import TableResponsibility from './settings/TableResponsibility';
import DineInSettings from './settings/DineInSettings';
import DocumentNumberingPage from './settings/DocumentNumberingPage';
import TakeAwaySettings from './settings/TakeAwaySettings';
import DeliverySettings from './settings/DeliverySettings';
import BusinessDetailsPage from './settings/BusinessDetailsPage';
import AppearancePage from './settings/AppearancePage';
import RegisterReportsPage from './settings/RegisterReportsPage';
import CashMovementsAdminPage from './register/cashMovements/CashMovementsAdminPage';
import DailyDishesAdminPage from './dailyDishes/DailyDishesAdminPage';
import PrintingPage from './printers/PrintingPage';
import CallHistory from './CallHistory';
import CustomerManagement from './CustomerManagement';
import CatalogPage from './catalog/CatalogPage';
import OrgSyncBanner from './OrgSyncBanner';
import EmployeesPanel from './employees/EmployeesPanel';
import { LogOut } from 'lucide-react';

// Συσκευές ταμείου: σπάνια σελίδα (εγκατάσταση οθόνης/modem) — φορτώνεται μόνο όταν ανοίξει.
const CashierDevicesPage = React.lazy(() => import('./settings/CashierDevicesPage'));

const StoreAdminDashboard: React.FC = () => {
  const { store, employee, employeeLogout } = useAuth();
  const { t } = useTranslation(['settings', 'common']);
  // Σάλα: ίδιοι κανόνες με το μενού — αποθηκευμένη καρτέλα δεν ανοίγει σελίδα που το μενού δεν δείχνει.
  const dineIn = store?.dine_in === true;
  const dineInAdmin = dineIn && isStoreAdminRole(employee);

  // Η καρτέλα της προηγούμενης φόρτωσης (F5, σελιδοδείκτης /products)· ids παλιότερης έκδοσης → η διάδοχός τους.
  const [activeTab, setActiveTab] = useState<StoreTabId>(() => resolveStoreTab(localStorage.getItem('storeAdminActiveTab')));
  // «Αρχική» στο μενού ενώ είσαι ήδη σε αυτή (π.χ. σε μια αναφορά της): πίσω στην Επισκόπηση (HomePage resetKey).
  const [homeResetKey, setHomeResetKey] = useState(0);

  useEffect(() => {
    // Clear the saved tab after using it
    return () => {
      localStorage.removeItem('storeAdminActiveTab');
    };
  }, [store?.id]);

  // Η σελίδα που κλείνει χάνει ό,τι δεν αποθηκεύτηκε (utils/unsavedChanges): ρώτα πρώτα, αλλιώς μένεις.
  const handleLogout = () => {
    if (hasUnsavedChanges() && !confirmDiscard()) return;
    employeeLogout();
    // Will go back to employee selection (handled by Login component)
  };

  const handleTabClick = (tab: string) => {
    if (tab === activeTab) {
      if (tab === 'dashboard') setHomeResetKey(k => k + 1);
      return;
    }
    if (hasUnsavedChanges() && !confirmDiscard()) return;
    const next = resolveStoreTab(tab);
    setActiveTab(next);
    localStorage.setItem('storeAdminActiveTab', next);
  };

  return (
    <div className="min-h-screen bg-surface-0">
      {/* Header */}
      <div className="role-header bg-surface-1 border-b border-line">
        <div className="w-full px-3 sm:px-4">
          <div className="flex justify-between items-center h-14">
            {/* Logo and Brand */}
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <img 
                src={logo} 
                alt="Xentrix Flow Logo" 
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex-shrink-0"
              />
              <div className="min-w-0">
                <h1 className="text-base sm:text-lg font-bold text-t1 truncate">Xentrix Flow</h1>
                <p className="text-xs text-t3 truncate">{store?.store_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <ThemeToggle />
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-t1">{employee?.name}</p>
                <p className="text-xs text-t3">{t('settings:employeesPanel.adminRole')}</p>
              </div>
              <button
                onClick={handleLogout}
                className="w-9 h-9 rounded-lg bg-surface-2 hover:bg-red-600/20 text-t3 hover:text-red-600 dark:hover:text-red-400 flex items-center justify-center transition-all active:scale-95"
                title={t('auth:logout', { defaultValue: 'Αποσύνδεση' })}
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Bar + Main Content — κλασική: καρτέλες από πάνω· νέα εμφάνιση: πλαϊνό μενού δίπλα (StoreAdminLayout) */}
      <StoreAdminLayout activeTab={activeTab} onTabClick={handleTabClick}>
        {/* Org Sync Notifications */}
        <OrgSyncBanner />

        {/* Αρχική «Ήσυχο ταμπλό» (13/10/2026) — στη θέση του παλιού πίνακα αναλυτικών */}
        {activeTab === 'dashboard' && (
          <HomePage onNavigate={handleTabClick} resetKey={homeResetKey} />
        )}

        {/* Orders Tab — date-filtered list (today by default) with cancel
            (cancels the linked receipt too). Same shape as the HQ panel. */}
        {activeTab === 'orders' && (
          <StoreOrdersPanel />
        )}

        {/* Waste Tab — μόνο για καταστήματα με waste_enabled (το tab κρύβεται αλλιώς) */}
        {activeTab === 'waste' && (
          <StoreWastePanel />
        )}

        {/* Employees Tab — υπάλληλοι, PIN και δικαιώματα ανά άτομο σε μία οθόνη (components/employees) */}
        {activeTab === 'employees' && (
          <EmployeesPanel />
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <CustomerManagement />
        )}

        {/* Κατάλογος: προϊόντα, κατηγορίες και βιβλιοθήκες (ομάδες επιλογών/μεγεθών, υλικά) */}
        {activeTab === 'catalog' && (
          <CatalogPage />
        )}

        {/* Warehouse Tab */}
        {activeTab === 'warehouse' && (
          <WarehouseManagement />
        )}

        {/* Platform Integration Tab */}
        {activeTab === 'platforms' && (
          <PlatformIntegration />
        )}

        {/* Promotions Tab */}
        {activeTab === 'promotions' && (
          <PromotionsManagement />
        )}

        {/* ECR & Fiscal Documents Tab */}
        {activeTab === 'ecr' && (
          <ECRFiscalDocuments />
        )}

        {/* Αρίθμηση παραστατικών — μόνο με Oxygen, όπως το μενού */}
        {activeTab === 'numbering' && storeUsesOxygen(store) && (
          <DocumentNumberingPage />
        )}

        {/* POS Terminals Tab */}
        {activeTab === 'pos' && (
          <POSTerminals />
        )}

        {/* Ζ και Χ (αναφορές ταμείου) */}
        {activeTab === 'registerreports' && (
          <RegisterReportsPage />
        )}

        {/* Κινήσεις ταμείου (έξοδα/αναλήψεις/καταθέσεις, CSV λογιστή, κατηγορίες) — όπως το μενού: διαχειριστής
            και ανοιχτός διακόπτης του καταστήματος. */}
        {activeTab === 'cashmovements' && store?.cash_movements_enabled === true && isStoreAdminRole(employee) && (
          <CashMovementsAdminPage />
        )}

        {/* Πιάτα ημέρας (διακόπτης + κατηγορία + «Σήμερα») — ο διαχειριστής, και με κλειστό διακόπτη (ζει εδώ). */}
        {activeTab === 'dailydishes' && isStoreAdminRole(employee) && (
          <DailyDishesAdminPage />
        )}

        {/* Κατάστημα: οι παλιές «Ρυθμίσεις» (ο «Λογαριασμός» έφυγε 13/10/2026) */}
        {activeTab === 'business' && (
          <BusinessDetailsPage />
        )}
        {activeTab === 'appearance' && (
          <AppearancePage />
        )}

        {/* Σάλα σε τρεις σελίδες (13/10/2026) — μόνο σε καταστήματα με τραπέζια */}
        {activeTab === 'floorplan' && dineIn && (
          <FloorPlanEditor />
        )}
        {activeTab === 'tables' && dineInAdmin && (
          <TableResponsibility />
        )}
        {activeTab === 'salasettings' && dineInAdmin && (
          <DineInSettings />
        )}

        {/* TakeAway Settings Tab - Show if takeaway is enabled */}
        {activeTab === 'takeawaysettings' && store?.takeaway && (
          <TakeAwaySettings />
        )}

        {/* Delivery: με οποιονδήποτε τρόπο delivery, όπως το μενού (ως 12/10 μόνο με δικό μας delivery:
            κατάστημα μόνο με efood/Wolt έβλεπε κενή σελίδα). */}
        {activeTab === 'deliverysettings' && storeHasDelivery(store) && (
          <DeliverySettings />
        )}


        {/* Εκτυπωτές (14/10/2026): ένα «Φύλλο» — υπολογιστής ταμείου, εκτυπωτές, τι πάει πού, απόδειξη */}
        {activeTab === 'printers' && (
          <PrintingPage />
        )}

        {/* Συσκευές ταμείου (25/10/2026): οθόνη πελάτη + αναγνώριση κλήσης — όπως το μενού, μόνο ο διαχειριστής */}
        {activeTab === 'devices' && isStoreAdminRole(employee) && (
          <React.Suspense fallback={null}>
            <CashierDevicesPage />
          </React.Suspense>
        )}

        {/* Call History Tab */}
        {activeTab === 'callhistory' && (
          <CallHistory />
        )}
      </StoreAdminLayout>
    </div>
  );
};

export default StoreAdminDashboard;
